import { EllipsisOutlined, PlusOutlined, ReloadOutlined, ShareAltOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, Checkbox, Dropdown, Form, Input, Menu, message, Modal, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api';
import './cloud-drive-sync.css';

const { Option } = Select;
const { Title, Text } = Typography;

const SpaceManage = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [form] = Form.useForm();
  const [sharedModalVisible, setSharedModalVisible] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [sharedForm] = Form.useForm();

  // 过滤相关状态
  const [searchText, setSearchText] = useState('');
  const [sharedStatus, setSharedStatus] = useState('all');

  const fetchGroups = async () => {
    setLoading(true);
    try {
      // 使用新的API接口获取用户可访问的文档组
      const res = await axios.get('/api/user-accessible-document-groups/');
      const groupsData = res.data.results || res.data;

      // 为每个组获取文档信息以显示共享状态
      const groupsWithDocs = await Promise.all(
        groupsData.map(async (group) => {
          try {
            const sharedRes = await axios.get(`/api/document-groups/${group.id}/shared-settings/`);
            return {
              ...group,
              documents: sharedRes.data.documents || []
            };
          } catch (err) {
            return {
              ...group,
              documents: []
            };
          }
        })
      );

      setGroups(groupsWithDocs);
    } catch (err) {
      message.error('获取空间列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const res = await axios.get('/api/users/groups/');
      setUserGroups(res.data.results || res.data);
    } catch (err) {
      message.error('获取用户组列表失败');
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchUserGroups();
  }, []);

  // 获取空间的共享状态
  const getGroupSharedStatus = (group) => {
    if (!group.documents || group.documents.length === 0) {
      return { status: 'none', text: '无文档', color: 'default' };
    }

    const hasGlobalShared = group.documents.some(doc => doc.is_global_shared);
    const hasGroupShared = group.documents.some(doc =>
      doc.shared_with_spaces && doc.shared_with_spaces.length > 0
    );

    if (hasGlobalShared) {
      return { status: 'global', text: '全局共享', color: 'red' };
    } else if (hasGroupShared) {
      return { status: 'group', text: '组共享', color: 'blue' };
    } else {
      return { status: 'none', text: '未共享', color: 'default' };
    }
  };

  // 应用过滤条件
  const getFilteredGroups = () => {
    let filteredData = [...groups];

    // 搜索过滤
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filteredData = filteredData.filter(group =>
        group.name.toLowerCase().includes(searchLower) ||
        (group.description && group.description.toLowerCase().includes(searchLower))
      );
    }

    // 共享状态过滤
    if (sharedStatus !== 'all') {
      filteredData = filteredData.filter(group => {
        const status = getGroupSharedStatus(group);
        return status.status === sharedStatus;
      });
    }

    return filteredData;
  };

  const filteredGroups = getFilteredGroups();

  const handleAdd = () => {
    setEditingGroup(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    form.setFieldsValue({ name: group.name, description: group.description });
    setModalVisible(true);
  };

  const handleDelete = async (group) => {
    try {
      await axios.delete(`/api/document-groups/${group.id}/`);
      message.success('删除成功');
      fetchGroups();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingGroup) {
        await axios.put(`/api/document-groups/${editingGroup.id}/`, values);
        message.success('编辑成功');
      } else {
        await axios.post('/api/document-groups/', values);
        message.success('新建成功');
      }
      setModalVisible(false);
      fetchGroups();
    } catch (err) {
      // 校验失败或请求失败
    }
  };

  const handleShare = (group) => {
    setSelectedGroup(group);
    setSharedModalVisible(true);
    // 获取当前组的共享设置
    fetchGroupSharedSettings(group.id, group);
  };

  // 处理成员管理 - 跳转到独立页面
  const handleMemberManage = (group) => {
    navigate(`/space/${group.id}/members`);
  };

  const fetchGroupSharedSettings = async (groupId, group) => {
    try {
      const res = await axios.get(`/api/document-groups/${groupId}/shared-settings/`);
      const data = res.data;

      // 分析该组下文档的共享状态
      let hasGlobalShared = false;
      let sharedGroupIds = [];

      if (data.documents && data.documents.length > 0) {
        // 检查是否有全局共享的文档
        hasGlobalShared = data.documents.some(doc => doc.is_global_shared);

        // 收集所有共享的空间ID
        const allSharedSpaces = new Set();
        data.documents.forEach(doc => {
          if (doc.shared_with_spaces) {
            doc.shared_with_spaces.forEach(spaceId => allSharedSpaces.add(spaceId));
          }
        });
        sharedGroupIds = Array.from(allSharedSpaces);
      }

      // 设置表单数据
      sharedForm.setFieldsValue({
        shared_spaces: sharedGroupIds,
        is_global_shared: hasGlobalShared
      });

      // 存储文档数据用于显示，确保group对象完整
      setSelectedGroup({ ...group, documents: data.documents });
    } catch (err) {
      message.error('获取共享设置失败');
    }
  };

  const handleSharedModalOk = async () => {
    try {
      const values = await sharedForm.validateFields();
      console.log('共享设置表单值:', values);
      console.log('选中的组:', selectedGroup);
      console.log('选中的组ID:', selectedGroup?.id);

      // 检查selectedGroup是否存在且有id
      if (!selectedGroup || !selectedGroup.id) {
        console.error('selectedGroup或selectedGroup.id不存在:', selectedGroup);
        message.error('选中的空间信息不完整，请重新选择');
        return;
      }

      // 批量设置该组下所有文档的共享属性
      if (selectedGroup?.documents && selectedGroup.documents.length > 0) {
        console.log('开始批量设置文档共享属性，文档数量:', selectedGroup.documents.length);

        const promises = selectedGroup.documents.map(doc => {
          const requestData = {
            document_id: doc.id,
            is_global_shared: values.is_global_shared,
            shared_with_spaces: values.shared_spaces || []
          };
          console.log(`设置文档 ${doc.id} 的共享属性:`, requestData);

          return axios.post(`/api/document-groups/${selectedGroup.id}/shared-settings/`, requestData)
            .then(response => {
              console.log(`文档 ${doc.id} 设置成功:`, response.data);
              return response;
            })
            .catch(error => {
              console.error(`文档 ${doc.id} 设置失败:`, error.response?.data || error.message);
              throw error;
            });
        });

        await Promise.all(promises);
        message.success('共享设置已保存');
        setSharedModalVisible(false);

        // 刷新空间列表以更新共享状态
        fetchGroups();
      } else {
        console.log('该空间下暂无文档，仅保存设置');
        message.success('共享设置已保存');
        setSharedModalVisible(false);
      }
    } catch (err) {
      console.error('保存共享设置失败:', err);
      console.error('错误详情:', err.response?.data);
      message.error(`保存共享设置失败: ${err.response?.data?.detail || err.message}`);
    }
  };

  const columns = [
    {
      title: '空间名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <a style={{ fontWeight: 500 }} onClick={() => handleMemberManage(record)}>{text}</a>
      )
    },
    {
      title: '文档数量/份',
      dataIndex: 'document_count',
      key: 'document_count',
      render: (count) => <Tag color="#3b6ed6">{count}</Tag>,
    },
    {
      title: '人数/个',
      dataIndex: 'member_count',
      key: 'member_count',
      render: (count, record) => (
        <Tag 
          color="#6bc200" 
          style={{ cursor: 'pointer' }}
          onClick={() => handleMemberManage(record)}
        >
          {count}
        </Tag>
      ),
    },
    {
      title: '共享状态',
      key: 'shared_status',
      render: (_, record) => {
        const status = getGroupSharedStatus(record);
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => <span>{text?.slice(0, 10)}</span>,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="edit" onClick={() => handleEdit(record)}>
                编辑
              </Menu.Item>
              <Menu.Item key="members" onClick={() => handleMemberManage(record)}>
                <TeamOutlined /> 成员管理
              </Menu.Item>
              <Menu.Item key="share" onClick={() => handleShare(record)}>
                <ShareAltOutlined /> 共享设置
              </Menu.Item>
              <Menu.Item key="delete">
                <Popconfirm title="确定删除该空间？" onConfirm={() => handleDelete(record)} okText="确定" cancelText="取消">
                  删除
                </Popconfirm>
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button type="text" icon={<EllipsisOutlined style={{ fontSize: 20 }} />} style={{ boxShadow: 'none', border: 'none', background: 'none', padding: 0, margin: 0 }} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 32, minHeight: 500 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space>
          <Input.Search
            placeholder="搜索空间名称"
            style={{ width: 200 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
          <Select
            placeholder="共享状态"
            style={{ width: 120 }}
            value={sharedStatus}
            onChange={setSharedStatus}
            allowClear
          >
            <Option value="all">全部</Option>
            <Option value="global">全局共享</Option>
            <Option value="group">组共享</Option>
            <Option value="none">未共享</Option>
          </Select>
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchGroups}>
            刷新
          </Button>
          <Button type="primary" className="cloud-primary-btn" icon={<PlusOutlined />} onClick={handleAdd}>
            新建空间
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredGroups}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
        }}
      />

      {/* 新建/编辑空间弹框 */}
      <Modal
        title={editingGroup ? '编辑空间' : '新建空间'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="空间名称" rules={[{ required: true, message: '请输入空间名称' }]}>
            <Input placeholder="请输入空间名称" />
          </Form.Item>
          <Form.Item name="description" label="空间描述">
            <Input.TextArea placeholder="请输入空间描述" rows={3} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" className="cloud-primary-btn" onClick={handleModalOk}>
              {editingGroup ? '保存' : '创建'}
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalVisible(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 共享设置弹框 */}
      <Modal
        title={`共享设置 - ${selectedGroup?.name}`}
        open={sharedModalVisible}
        onOk={handleSharedModalOk}
        onCancel={() => setSharedModalVisible(false)}
        destroyOnHidden
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <h4>该空间下的文档共享设置</h4>
          <p style={{ color: '#666', fontSize: '12px' }}>
            您可以设置该空间下所有文档的共享范围，普通用户将能够看到已共享的文档。
          </p>
        </div>

        {selectedGroup?.documents && selectedGroup.documents.length > 0 ? (
          <div style={{ marginBottom: 16 }}>
            <h5>文档列表：</h5>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: 8 }}>
              {selectedGroup.documents.map(doc => (
                <div key={doc.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <span>{doc.title}</span>
                  <div>
                    {doc.is_global_shared && (
                      <Tag color="red" size="small">全局共享</Tag>
                    )}
                    {doc.shared_with_space_names && doc.shared_with_space_names.length > 0 && (
                      <Tag color="blue" size="small">空间共享</Tag>
                    )}
                    {!doc.is_global_shared && (!doc.shared_with_space_names || doc.shared_with_space_names.length === 0) && (
                      <Tag color="default" size="small">未共享</Tag>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 16, color: '#999' }}>
            该空间下暂无文档
          </div>
        )}

        <Form form={sharedForm} layout="vertical">
          <Form.Item name="is_global_shared" valuePropName="checked">
            <Checkbox>全局共享（所有用户可见）</Checkbox>
          </Form.Item>
          <Form.Item name="shared_spaces" label="共享给空间">
            <Select
              mode="multiple"
              placeholder="选择要共享的空间"
              style={{ width: '100%' }}
            >
              {groups.map(space => (
                <Option key={space.id} value={space.id}>{space.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" className="cloud-primary-btn" onClick={handleSharedModalOk}>
              保存设置
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setSharedModalVisible(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SpaceManage;
